import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const useAutoLogout = (timeoutMinutes = 10, redirectPath = '/login') => {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  
  // Convert minutes to milliseconds
  const timeoutMs = timeoutMinutes * 60 * 1000;

  const logout = useCallback(() => {
    // Clear any existing authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userData');
    localStorage.removeItem('currentUser');
    sessionStorage.clear();
    
    // Navigate to login page
    navigate(redirectPath);
  }, [navigate, redirectPath]);

  const resetTimer = useCallback(() => {
    // Clear existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set logout timer
    timeoutRef.current = setTimeout(logout, timeoutMs);
  }, [timeoutMs, logout]);

  const handleUserActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    // List of events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners for user activity
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Start the initial timer
    resetTimer();

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });

      // Clear timer
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [handleUserActivity, resetTimer]);

  // Return functions that components might need
  return {
    resetTimer,
    logout
  };
};

export default useAutoLogout;
